// Языки контрибьюторского кабинета.
//
// Штаб работает по-русски и переводов не требует — их экраны остаются как есть.
// Контрибьюторы разбросаны по LatAm, США и Азии, поэтому их поверхность живёт
// на четырёх языках: en как общий, es и pt для тех, кто снимает, и ru — часть
// контрибьюторов тоже говорит по-русски (Tim, 12.08: «язык можно добавить»).
//
// Язык берётся из профиля (contributors.ui_lang), а не из браузера: телефон
// может быть чужим, язык системы — случайным, а человека заводит админ, который
// знает, на каком языке с ним говорить.
//
// Словарь плоский и без интерполяции-фреймворка: подстановок мало, а функция с
// одним аргументом читается на месте лучше, чем шаблонный движок.
export type Lang = 'en' | 'es' | 'pt' | 'ru';

export const LANGS: Lang[] = ['en', 'es', 'pt', 'ru'];

export const asLang = (v: string | null | undefined): Lang =>
  v === 'es' || v === 'pt' || v === 'ru' ? v : 'en';

// F4 profile-menu language switcher: there is no self-service "update my
// profile" endpoint yet (contributors.ui_lang is set by the admin who adds
// them), so a manual override lives in localStorage and simply wins over
// me.uiLang until one is added — see apps/vista-web/src/main.tsx.
const LANG_STORAGE_KEY = 'vista.lang';

export const getStoredLang = (): Lang | null => {
  const v = localStorage.getItem(LANG_STORAGE_KEY);
  return v === 'en' || v === 'es' || v === 'pt' || v === 'ru' ? v : null;
};

export const setStoredLang = (lang: Lang): void => {
  localStorage.setItem(LANG_STORAGE_KEY, lang);
};

type Dict = Record<string, string>;

const en: Dict = {
  'staff.build': 'build',
  'pace.until': 'until {date} · {days} d left · pace',
  'v.accrued': ' Accrued ${amount}',
  'v.reversed': ', reversed ${amount}',
  'v.applied': 'Applied: {n}.{money}',
  'v.skipped': ' Not applied: {n} — {list}',
  'v.truncated': 'The list is longer than we parse at once: {n} line(s) did not fit. Apply this chunk and paste the rest.',
  'q.selected': 'Selected: {n}',
  'q.moved': 'Moved: {n}',
  'q.skipped': 'skipped: {n}',
  'q.accrued': 'accrued: ${amount}',
  'q.reversed': 'reversed: ${amount}',
  'q.unrated': 'no rate (not accrued): {n} — set a rate on the person card',
  'q.diag.drops': '{n} drops',
  'mng.th.pendingShort': 'In review',
  'mng.noRateCount': 'no rate: {n}',
  'shoot.audio.saved': 'Audio recorded as a separate track ({kb} KB).',
  'shoot.audio.failed': 'Audio was recorded but did not upload — tell the team.',
  'mine.noRejected': 'No rejected recordings.',
  'mine.noRecords': 'No recordings yet — they appear after your first shoot.',

  'cp.notFound': 'We could not find this person — they may be in another admin’s pool.',
  'cp.backToPool': 'Back to the pool',
  'cp.loading': 'Loading…',
  'cp.crumb': '← My pool',
  'cp.noCountry': 'country not set',
  'cp.inviteLink': 'Sign-in link',
  'cp.pwd.generating': 'Generating…',
  'cp.pwd.new': 'New cabinet password',
  'cp.unblocking': 'Unblocking…',
  'cp.unblock': 'Unblock',
  'cp.blockConfirm': 'Block for sure?',
  'cp.blocking': 'Blocking…',
  'cp.yes': 'Yes',
  'cp.cancel': 'Cancel',
  'cp.block': 'Block',
  'cp.cabinet': 'Filming cabinet',
  'cp.linked': 'linked to the staff account',
  'cp.linkedTail': '— this person switches between cabinets from the profile menu.',
  'cp.notLinked': 'is not linked to anyone. If this is the same person who works in the office, link them — they will get the cabinet switcher.',
  'cp.unlinking': 'Unlinking…',
  'cp.unlink': 'Unlink from me',
  'cp.linking': 'Linking…',
  'cp.itsMe': 'That is me',
  'cp.journal': 'What was done with this person and their clips',
  'cp.th.when': 'When',
  'cp.th.who': 'Who',
  'cp.th.what': 'What',
  'cp.th.details': 'Details',
  'cp.system': 'system',
  'cp.noRateWarn': 'No rate set — this person’s accepted hours will not accrue until there is one.',
  'cp.data': 'Details',
  'cp.name': 'Name',
  'cp.country': 'Country (2 letters)',
  'cp.city': 'City',
  'cp.phone': 'Phone',
  'cp.uiLang': 'Interface language',
  'cp.saving': 'Saving…',
  'cp.save': 'Save',
  'cp.saved': 'Saved',
  'cp.saveFailed': 'Not saved:',
  'cp.noRate': 'no rate set',
  'cp.earnedAt': 'earning at {rate}/h',
  'cp.byDay': 'By day',
  'cp.records': 'Recordings',
  'cp.tab.all': 'All ({n})',
  'cp.tab.rejected': 'Rejected ({n})',
  'cp.noRejected': 'No rejects — good.',
  'cp.noRecords': 'No recordings yet.',
  'cp.th.file': 'File',
  'cp.th.filmed': 'Filmed',
  'cp.th.length': 'Length',
  'cp.th.status': 'Status',
  'cp.th.topic': 'Topic',
  'cp.th.integrity': 'Integrity',
  'cp.th.reason': 'Reason',
  'cp.noName': 'no name',
  'cp.owners': 'Who ran them',
  'cp.now': 'now',
  'cp.creds.copied': '✓ Copied',
  'cp.creds.copy': 'Copy login and password',
  'cp.creds.hide': 'Hide',
  'cp.creds.note': 'New access to vista.weern.com — shown once.',
  'cp.act.capture_open': 'recording started',
  'cp.act.capture_finish': 'recording finished',
  'cp.act.capture_integrity_failed': 'failed the integrity gate',
  'cp.act.capture_salvaged': 'salvaged from an abandoned upload',
  'cp.act.clip_accept': 'client accepted',
  'cp.act.clip_reject': 'client rejected',
  'cp.act.clip_deliver': 'shipped to client',
  'cp.act.clip_pending': 'awaiting verdict',
  'cp.act.clip_requeue': 'sent back to narration',
  'cp.act.clip_queue_voiceover': 'into the narration queue',
  'cp.act.rate_change': 'rate changed',
  'cp.act.status_blocked': 'blocked',
  'cp.act.status_active': 'unblocked',
  'cp.act.password_reset': 'new password issued',
  'cp.act.payout': 'payout',
  'cp.act.adjust': 'adjustment',
  'cp.rev.recording': 'recording',
  'cp.rev.filmed': 'filmed',
  'cp.rev.needs_voiceover': 'awaiting narration',
  'cp.rev.voiced': 'narrated',
  'cp.rev.delivered': 'shipped',
  'cp.rev.pending': 'awaiting verdict',
  'cp.rev.accepted': 'accepted',
  'cp.rev.rejected': 'rejected',

  'pace.title': 'Plan to deadline',
  'pace.intro': 'Set an overall goal in hours of accepted material and a deadline — the pace and the per-day number are worked out from there.',
  'pace.edit': 'change the goal',
  'pace.loadFailed': 'Could not load the plan — try refreshing the page.',
  'pace.noGoal': 'The manager has not set a goal yet.',
  'pace.onTrack': 'On track',
  'pace.behind': 'Behind',
  'pace.perDay': 'h/day',
  'pace.need': 'need to accept',
  'pace.perDay7': 'h/day (7 d)',
  'pace.accepted': 'accepted',
  'pace.of': 'h of',
  'pace.h': 'h',
  'pace.goal': 'Goal, hours of accepted material',
  'pace.goalPh': 'e.g. 500',
  'pace.deadline': 'Deadline',
  'pace.cancel': 'Cancel',
  'pace.saving': 'Saving…',
  'pace.save': 'Save the goal',
  'pace.err.goal': 'The goal must be a positive number of hours.',
  'pace.err.deadline': 'Deadline — a date in YYYY-MM-DD format.',
  'pace.err.save': 'Could not save the goal.',
  'live.nobody': 'Nobody is filming right now',
  'live.now': '{n} {word} right now',
  'live.recording.one': 'recording',
  'live.recording.many': 'recording',
  'live.clipsToday': 'clips today',
  'live.filmedToday': 'filmed today',
  'live.peopleFilmed': 'people filmed',
  'live.mb': '{n} MB',
  'inv.title': 'Sign-in link · {name}',
  'inv.note': 'Valid for 7 days. Send it to the person — they open it on their phone and land straight in the cabinet, no password needed.',
  'inv.copied': 'Copied',
  'inv.copy': 'Copy the link',
  'inv.failed': 'Could not create the link. Try again.',
  'inv.creating': 'Creating…',
  'inv.close': 'Close',
  'player.preparing': 'Preparing playback…',

  'fin.type.accrual': 'accrued',
  'fin.type.advance': 'advance',
  'fin.type.payout': 'payout',
  'fin.type.adjustment': 'adjustment',
  'fin.loading': 'Loading…',
  'fin.loadFailed': 'Could not load balances.',
  'fin.historyFailed': 'Could not load the history.',
  'fin.moreFailed': 'Could not load more history.',
  'fin.tile.owed': 'owed in total',
  'fin.tile.withDebt': 'with an open balance',
  'fin.tile.noRate': 'accepted but no rate',
  'fin.contributors': 'Contributors',
  'fin.staff': 'Staff (margin)',
  'fin.nobody': 'Nobody yet.',
  'fin.th.who': 'Who',
  'fin.th.rate': 'Rate',
  'fin.th.accepted': 'Accepted, h',
  'fin.th.owed': 'Owed',
  'fin.noRate': 'not set',
  'fin.history': 'history →',
  'fin.owed': 'Owed:',
  'fin.rateSuffix': ' · rate {rate}/h',
  'fin.payAmount': 'Pay out, $',
  'fin.pay': 'Record a payout',
  'fin.adjust.hide': 'Hide adjustment',
  'fin.adjust': 'Adjustment',
  'fin.err.amount': 'Enter an amount greater than zero.',
  'fin.err.duplicatePayout': 'That payout is already recorded — the repeat was not saved.',
  'fin.err.notSaved': 'Not saved.',
  'fin.th.when': 'When',
  'fin.th.what': 'What',
  'fin.th.amount': 'Amount',
  'fin.th.note': 'Comment',
  'fin.th.by': 'By whom',
  'fin.noMoves': 'No movements yet.',
  'fin.more': 'More ({n} left)',
  'fin.close': 'Close',
  'fin.adjust.amount': 'Adjustment amount, $ (+ reduces the debt, − increases it)',
  'fin.adjust.reason': 'Reason (required)',
  'fin.err.adjustAmount': 'The amount must be non-zero (signed: + or −).',
  'fin.err.adjustReason': 'Give a reason for the adjustment — it cannot be saved without one.',
  'fin.err.duplicateAdjust': 'That adjustment is already recorded — the repeat was not saved.',
  'fin.saving': 'Saving…',
  'fin.adjust.apply': 'Apply the adjustment',

  'v.title': 'Client verdicts in bulk',
  'v.help': 'Paste the table as it is: file name, verdict, reason. Tabs, commas or semicolons all work; a header row and quotes do not get in the way. Nothing changes until you look at the preview and press Apply.',
  'v.close': 'Close',
  'v.parsing': 'Reading…',
  'v.check': 'Check the list',
  'v.willApply': 'Will apply:',
  'v.needAttention': '· need attention: {n} (will not apply)',
  'v.th.line': 'Line',
  'v.th.clip': 'Clip',
  'v.th.who': 'Filmed by',
  'v.th.verdict': 'Verdict',
  'v.th.problem': 'What is wrong',
  'v.notFound': 'not found',
  'v.accepted': 'accepted',
  'v.rejected': 'rejected',
  'v.ambiguous': 'matches several clips',
  'v.notInPool': 'clip not found in your pool',
  'v.badVerdict': 'the verdict in this line was not recognised',
  'v.back': 'Back to the text',
  'v.applying': 'Applying…',
  'v.apply': 'Apply {n}',
  'v.reason.integrity': 'integrity',
  'v.reason.no_narration': 'no narration',
  'v.reason.weak_narration': 'weak narration',
  'v.reason.hands_out_of_frame': 'hands out of frame',
  'v.reason.poor_light': 'poor light',
  'v.reason.motion_blur': 'motion blur',
  'v.reason.repetitive': 'repetitive',
  'v.reason.staged': 'staged',
  'v.reason.privacy': 'privacy',
  'v.reason.duplicate': 'duplicate',
  'v.reason.off_topic': 'off topic',
  'v.reason.other': 'other',

  'q.reject.title': 'Reject {count} clip(s)',
  'q.tab.needs_voiceover': 'To narrate',
  'q.tab.voiced': 'To ship',
  'q.tab.delivered': 'Shipped',
  'q.tab.pending': 'Awaiting verdict',
  'q.tab.accepted': 'Accepted',
  'q.tab.rejected': 'Rejected',
  'q.tab.quarantine': 'Quarantine',
  'q.reason.integrity': 'Broken recording (shorter than the timer)',
  'q.reason.no_narration': 'No narration',
  'q.reason.weak_narration': 'Narration does not match what is on screen',
  'q.reason.hands_out_of_frame': 'Hands out of frame',
  'q.reason.poor_light': 'Poor light',
  'q.reason.motion_blur': 'Motion blur, the action is not visible',
  'q.reason.repetitive': 'The same action over and over',
  'q.reason.staged': 'Acted out, staged',
  'q.reason.privacy': 'Privacy: people, documents, screens',
  'q.reason.duplicate': 'Duplicate of something already delivered',
  'q.reason.off_topic': 'Wrong topic',
  'q.reason.other': 'Other',
  'q.saveFailed': 'Not saved. Try again.',
  'q.voiced.uploaded': 'Narrated file uploaded — the clip moved to “To ship”.',
  'q.voiced.failed': 'The file did not upload.',
  'q.move.deliver': 'Ship to client',
  'q.move.accept': 'Client accepted',
  'q.move.requeue': 'Send back to narration',
  'q.move.rehab': 'Material is usable — to narration',
  'q.move.reject': 'Reject',
  'q.loading': 'Loading…',
  'q.loadFailed': 'Could not load the queue.',
  'q.search': 'Search: contributor, email, topic, clip id…',
  'q.verdicts': 'Verdicts in bulk',
  'q.allContributors': 'All contributors',
  'q.from': 'From',
  'q.to': 'to',
  'q.empty': 'Empty — nothing in this stack right now.',
  'q.th.contributor': 'Contributor',
  'q.th.topic': 'Topic',
  'q.th.length': 'Length',
  'q.th.size': 'Size',
  'q.th.source': 'Source',
  'q.th.integrity': 'Integrity',
  'q.th.filmed': 'Filmed',
  'q.th.shipped': 'Shipped',
  'q.th.files': 'Files',
  'q.th.reason': 'Reason',
  'q.source.upload': 'file',
  'q.source.recorder': 'recorder',
  'q.integrity.na': 'not applicable',
  'q.watch': '▶ watch',
  'q.file.raw': 'original',
  'q.file.voiced': 'narrated',
  'q.audio.pending': 'audio being mixed',
  'q.audio.none': 'no audio',
  'q.uploadVoiced': 'upload narration',
  'q.comment': 'Comment (visible to the contributor)',
  'q.cancel': 'Cancel',
  'q.close': 'Close',

  'mng.creds.copied': '✓ Copied',
  'mng.creds.copy': 'Copy login and password',
  'mng.creds.hide': 'Hide',
  'mng.creds.note': 'The password is shown once — after that only a reset.',
  'mng.creds.text': 'vista.weern.com\nlogin: {email}\npassword: {password}',
  'mng.saveFailed': 'Not saved:',
  'mng.admins': 'Admins and pools',
  'mng.loading': 'Loading…',
  'mng.th.staff': 'Person',
  'mng.th.role': 'Role',
  'mng.th.rate': 'Rate $/h',
  'mng.th.people': 'People',
  'mng.th.accepted': 'Accepted h',
  'mng.th.pending': 'In review',
  'mng.th.rejected': 'Rejected h',
  'mng.th.toContributors': 'To contributors',
  'mng.th.margin': 'Margin',
  'mng.disabled': 'disabled',
  'mng.unowned': 'Unowned',
  'mng.allAssigned': 'Every contributor belongs to a pool.',
  'mng.th.status': 'Status',
  'mng.th.assign': 'Assign',
  'mng.pickAdmin': '— pick an admin —',
  'mng.staff': 'Staff',
  'mng.search': 'Search by email or name…',
  'mng.filter.all': 'All',
  'mng.filter.enabled': 'Enabled',
  'mng.filter.disabled': 'Disabled',
  'mng.noStaff': 'No staff yet.',
  'mng.noMatch': 'Nobody matches the filter.',
  'mng.th.name': 'Name',
  'mng.th.pool': 'Pool',
  'mng.th.enabled': 'Enabled',
  'mng.th.seesMoney': 'Sees money',
  'mng.th.password': 'Password',
  'mng.pwd.new': 'new (min. 8)',
  'mng.pwd.generate': 'Generate',
  'mng.pwd.save': 'save',
  'mng.pwd.change': 'change',
  'mng.pwd.tooShort': 'The password must be at least 8 characters.',
  'mng.new.email': 'new staff email',
  'mng.new.name': 'name',
  'mng.new.password': 'password (min. 8)',
  'mng.new.generate': 'Generate a password',
  'mng.new.creating': 'Creating…',
  'mng.new.submit': '+ Staff member',
  'mng.role.viewer': 'viewer',
  'mng.role.admin': 'admin',
  'mng.role.manager': 'manager',

  'pool.err.noAdminRate': 'Contributor rates unlock once the manager sets your own rate.',
  'pool.err.aboveAdmin': 'A contributor rate must stay below yours{max}.',
  'pool.err.belowPool': 'This admin already has a rate of{max} in their pool — the admin rate must stay above it. Lower the contributor rates first.',
  'pool.ago.today': 'today',
  'pool.ago.yesterday': 'yesterday',
  'pool.ago.days': '{n} d ago',
  'pool.status.invited': 'invited',
  'pool.status.joined': 'signed in',
  'pool.status.active': 'active',
  'pool.status.blocked': 'blocked',
  'pool.tile.accepted': 'accepted',
  'pool.tile.pending': 'in review',
  'pool.tile.rejected': 'rejected',
  'pool.tile.revenue': 'revenue',
  'pool.tile.filming': 'filming',
  'pool.hours': 'h',
  'pool.strip.toDub': '🎙 {hours} h ({files} file(s)) waiting for narration.',
  'pool.strip.toShip': '📦 {hours} h ({files} file(s)) narrated and waiting to ship to the client.',
  'pool.filtered': 'Pool:',
  'pool.showAll': 'show everyone',
  'pool.add': '+ Add people',
  'pool.search': 'Search by email or name…',
  'pool.status.all': 'All statuses',
  'pool.owner.all': 'All admins',
  'pool.loading': 'Loading…',
  'pool.empty': 'Nobody in the pool yet — add people with the button above.',
  'pool.noMatch': 'Nobody matches the filter.',
  'pool.th.contributor': 'Contributor',
  'pool.th.status': 'Status',
  'pool.th.antifraud': 'Anti-fraud',
  'pool.th.owner': 'Owner',
  'pool.th.accepted': 'Accepted h',
  'pool.th.pending': 'Pending h',
  'pool.th.rejected': 'Rejected h',
  'pool.th.quality': 'Quality',
  'pool.th.rate': 'Rate $/h',
  'pool.th.earned': 'Earned',
  'pool.th.activity': 'Activity',
  'pool.th.revenue': 'Revenue',
  'pool.creds.copied': '✓ Copied',
  'pool.creds.copy': 'Copy all ({n})',
  'pool.creds.note': 'Cabinet credentials for vista.weern.com. Shown once — after that only a password reset on the person card.',
  'pool.dlg.title': 'Add contributors',
  'pool.dlg.emails': 'Emails (up to 25, comma- or newline-separated)',
  'pool.dlg.rate': 'Rate, $ per accepted hour (can be left empty and set later)',
  'pool.dlg.ratePh': 'e.g. 6',
  'pool.dlg.over25': '⚠ More than 25 — only the first 25 go through, the rest in a second batch.',
  'pool.dlg.result': 'Added: {created}, already there: {existing}.',
  'pool.dlg.foreign': 'These people already belong to another admin; transfers are done by a manager:',
  'pool.dlg.error': 'Error:',
  'pool.dlg.close': 'Close',
  'pool.dlg.cancel': 'Cancel',
  'pool.dlg.adding': 'Adding…',
  'pool.dlg.submit': 'Add ({n})',

  'staff.sub': '· dashboard',
  'staff.loading': 'Loading…',
  'staff.tab.team': 'Team',
  'staff.tab.queue': 'Queue',
  'staff.tab.money': 'Money',
  'staff.tab.manage': 'Manage',
  'staff.role.manager': 'Manager',
  'staff.role.admin': 'Admin',
  'staff.role.viewer': 'Viewer',
  'staff.role.contributor': 'Contributor',
  'staff.cabinet': 'Cabinet: {role}',
  'staff.password': 'Change password',
  'staff.password.new': 'New password',
  'staff.password.confirm': 'Repeat password',
  'staff.password.tooShort': 'At least 8 characters',
  'staff.password.mismatch': 'Passwords do not match',
  'staff.password.cancel': 'Cancel',
  'staff.password.save': 'Save',
  'staff.password.saving': 'Saving…',
  'staff.password.done': 'Password changed',
  'staff.logout': 'Log out',
  'staff.lang': 'Language',

  'login.lede': 'For staff and contributors. Access is granted by an admin.',
  'login.email': 'email',
  'login.password': 'password',
  'login.submit': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.failed': 'Sign-in failed: the email or password does not match. New credentials are issued by your manager.',
  'login.build': 'build',

  'shoot.pending.keep': 'Keep this recording',
  'shoot.pending.another': 'Film another task',
  'shoot.pending.kept': 'Saved. The team will look at it and decide — you do not need to do anything else.',
  'shoot.pending.keptEmpty': 'There was nothing to save in that recording — it can be filmed again.',
  'shoot.failed.kept': 'What you filmed is saved — the team will look at it. Nothing was deleted.',
  'pwa.install.title': 'Install Vista on your phone',
  'pwa.install.body': 'One tap to open, full screen for filming, no browser bars.',
  'pwa.install.button': 'Install',
  'pwa.install.how': 'How to install',
  'pwa.install.later': 'Not now',
  'pwa.ios.1': 'Tap the Share button in Safari (the square with an arrow).',
  'pwa.ios.2': 'Scroll down and tap "Add to Home Screen".',
  'pwa.ios.3': 'Tap "Add" — Vista appears on your home screen.',
  'pwa.update.body': 'A new version of Vista is ready.',
  'pwa.update.reload': 'Update now',
  'nav.shoot': 'Record',
  'nav.mine': 'My clips',
  'nav.logout': 'Log out',
  'nav.profile': 'Account menu',

  // F4 profile menu
  'profile.lang': 'Language',
  'profile.password': 'Change password',
  'profile.password.new': 'New password',
  'profile.password.confirm': 'Confirm password',
  'profile.password.tooShort': 'At least 8 characters',
  'profile.password.mismatch': 'Passwords do not match',
  'profile.password.save': 'Save',
  'profile.password.saving': 'Saving…',
  'profile.password.done': 'Password changed',
  'profile.password.cancel': 'Cancel',

  'nav.upload': 'Upload',
  'upload.title': 'Upload a video',
  'upload.hint': 'Filmed with your phone camera instead? Upload the file here — same topics, same pay. Use this if the in-app recorder gives you trouble.',
  'upload.file': 'Video file',
  'upload.progress': 'Uploading… {percent}%',
  'upload.more': 'Upload another',
  'upload.tooLong': 'The video is longer than 20 minutes — split it and upload the parts separately.',
  'upload.failed': 'The upload did not finish. Check your connection and try again — nothing was saved.',

  'shoot.title': 'Record a clip',
  'shoot.topic': 'Topic',
  'shoot.start': 'Start recording',
  'shoot.stop': 'Stop',
  'shoot.block': 'block',
  'shoot.uploaded': 'uploaded',
  'shoot.lagging': 'upload behind by',
  'shoot.finishing': 'Finishing the last chunk — keep this page open…',
  'shoot.length':
    'Record between {min} and {max} minutes. A long take is split into {seg}-minute blocks, so a dropped connection costs one block instead of the whole session.',

  'shoot.check.1': 'Camera at eye level, tilted down — your hands are visible',
  'shoot.check.2': 'Enough light, objects are clearly identifiable',
  'shoot.check.3': 'Real work, filmed from start to finish',
  'shoot.check.4': 'No repeating the same action over and over',
  'shoot.check.5': 'No bystanders, documents or screens in frame',

  'shoot.done.title': 'Done',
  'shoot.done.body':
    'Your clip is uploaded. We add narration and send it to the client — you will see the status in My clips.',
  'shoot.done.again': 'Record the next task',

  'shoot.failed.title': 'The recording was not saved',
  'shoot.failed.retake': 'Record again',
  'shoot.failed.quarantine':
    'This take did not pass the automatic check. It was saved for staff review — please film the task again.',
  'shoot.failed.integrity': 'The recording is damaged — the video is shorter than the timer. Please film it again.',
  'shoot.health.warn':
    'The camera is giving only {fps} frames per second — this take will be rejected. Add more light or move somewhere brighter, then restart.',
  'shoot.health.lag':
    'The phone is falling behind — the video is running short of the timer and this take will be rejected. Close other apps or restart the recording.',
  'shoot.health.help.title': 'What the percentage means',
  'shoot.health.help.what': 'How much of the last 30 seconds actually made it into the video.',
  'shoot.health.help.norm': '100% — everything recorded. Below 90% the clip will not be accepted.',
  'shoot.health.help.fix':
    "If it drops: your phone can't keep up — close other apps. A dark room hurts smoothness, not the percentage.",
  'shoot.failed.generic': 'Recording stopped. Please film it again.',
  'shoot.failed.camera': 'Could not start the camera. Check the camera permission.',
  'shoot.failed.upload': 'Could not finish the upload. Check your connection and try again.',

  'shoot.pending.title': 'Unfinished recording',
  'shoot.pending.body':
    'The previous clip did not finish uploading. You can discard it and film again — nothing is kept on the server.',
  'shoot.pending.discard': 'Discard and film again',

  'mine.title': 'My clips',
  'mine.accepted': 'accepted',
  'mine.pending': 'awaiting verdict',
  'mine.rejected': 'not accepted',
  'mine.filmed': 'filmed',
  'mine.earned': 'earned',
  'mine.norate': 'rate not set yet',
  'mine.perhour': 'at {rate}/h',
  'mine.empty': 'Nothing yet — record your first clip.',
  'mine.min': 'min',
  'mine.hours': 'h',
  'mine.byDay': 'By day',
  'mine.all': 'All',
  'mine.rejectedTab': 'Not accepted',
  'mine.pendingNote': '{hours} still waiting for the client — they move to accepted once the verdict arrives.',
  'mine.noname': 'no name',
  'mine.col.file': 'File',
  'mine.col.when': 'Filmed',
  'mine.col.length': 'Length',
  'mine.col.status': 'Status',
  'mine.col.topic': 'Topic',
  'mine.col.reason': 'Reason',
  'mine.play': 'Watch',
  'mine.player.close': 'Close',

  'status.recording': 'recording',
  'status.filmed': 'filmed',
  'status.needs_voiceover': 'preparing narration',
  'status.voiced': 'narrated',
  'status.delivered': 'sent to client',
  'status.pending': 'awaiting verdict',
  'status.accepted': 'accepted',
  'status.rejected': 'not accepted',
  'status.quarantine': 'under review',
  'mine.ledger.title': 'Earnings detail',
  'mine.ledger.what': 'For',
  'mine.ledger.amount': 'Amount',

  'err.tasks': 'Could not load the topic list — try reloading the page.',
  'err.stats': 'Stats are unavailable right now — try again a bit later.',
  'loading': 'Loading…',
};

const es: Dict = {
  'shoot.audio.saved': 'El audio se grabó en una pista aparte ({kb} KB).',
  'shoot.audio.failed': 'El audio se grabó pero no se envió — avisa al equipo.',
  'mine.noRejected': 'No hay grabaciones rechazadas.',
  'mine.noRecords': 'Todavía no hay grabaciones — aparecerán tras tu primera sesión.',

  'login.lede': 'Para el equipo y los colaboradores. El acceso lo concede un administrador.',
  'login.email': 'correo',
  'login.password': 'contraseña',
  'login.submit': 'Entrar',
  'login.signingIn': 'Entrando…',
  'login.failed': 'No se pudo entrar: el correo o la contraseña no coinciden. Las credenciales nuevas las entrega tu responsable.',
  'login.build': 'versión',

  'shoot.pending.keep': 'Guardar esta grabación',
  'shoot.pending.another': 'Grabar otra tarea',
  'shoot.pending.kept': 'Guardado. El equipo lo revisará y decidirá — no tienes que hacer nada más.',
  'shoot.pending.keptEmpty': 'No había nada que guardar en esa grabación — se puede grabar de nuevo.',
  'shoot.failed.kept': 'Lo que grabaste está guardado — el equipo lo revisará. No se borró nada.',
  'pwa.install.title': 'Instala Vista en tu teléfono',
  'pwa.install.body': 'Se abre con un toque, pantalla completa para grabar, sin barras del navegador.',
  'pwa.install.button': 'Instalar',
  'pwa.install.how': 'Cómo instalar',
  'pwa.install.later': 'Ahora no',
  'pwa.ios.1': 'Toca el botón Compartir en Safari (el cuadrado con la flecha).',
  'pwa.ios.2': 'Baja y toca "Añadir a pantalla de inicio".',
  'pwa.ios.3': 'Toca "Añadir" — Vista aparece en tu pantalla de inicio.',
  'pwa.update.body': 'Hay una nueva versión de Vista.',
  'pwa.update.reload': 'Actualizar',
  'nav.shoot': 'Grabar',
  'nav.mine': 'Mis videos',
  'nav.logout': 'Salir',
  'nav.profile': 'Menú de cuenta',

  'profile.lang': 'Idioma',
  'profile.password': 'Cambiar contraseña',
  'profile.password.new': 'Contraseña nueva',
  'profile.password.confirm': 'Confirma la contraseña',
  'profile.password.tooShort': 'Al menos 8 caracteres',
  'profile.password.mismatch': 'Las contraseñas no coinciden',
  'profile.password.save': 'Guardar',
  'profile.password.saving': 'Guardando…',
  'profile.password.done': 'Contraseña actualizada',
  'profile.password.cancel': 'Cancelar',

  'nav.upload': 'Subir',
  'upload.title': 'Subir un video',
  'upload.hint': '¿Grabaste con la cámara del teléfono? Sube el archivo aquí — mismos temas, mismo pago. Úsalo si el grabador de la app te da problemas.',
  'upload.file': 'Archivo de video',
  'upload.progress': 'Subiendo… {percent}%',
  'upload.more': 'Subir otro',
  'upload.tooLong': 'El video dura más de 20 minutos — divídelo y sube las partes por separado.',
  'upload.failed': 'La subida no terminó. Revisa la conexión e inténtalo de nuevo — no se guardó nada.',

  'shoot.title': 'Grabar un video',
  'shoot.topic': 'Tema',
  'shoot.start': 'Empezar a grabar',
  'shoot.stop': 'Detener',
  'shoot.block': 'bloque',
  'shoot.uploaded': 'subido',
  'shoot.lagging': 'la subida va con retraso de',
  'shoot.finishing': 'Subiendo el último fragmento — no cierres esta página…',
  'shoot.length':
    'Graba entre {min} y {max} minutos. Una toma larga se divide en bloques de {seg} minutos, así una caída de conexión cuesta un bloque y no toda la sesión.',

  'shoot.check.1': 'Cámara a la altura de los ojos, inclinada hacia abajo — se ven tus manos',
  'shoot.check.2': 'Luz suficiente, los objetos se distinguen con claridad',
  'shoot.check.3': 'Trabajo real, grabado de principio a fin',
  'shoot.check.4': 'Sin repetir la misma acción una y otra vez',
  'shoot.check.5': 'Sin personas ajenas, documentos ni pantallas en cuadro',

  'shoot.done.title': 'Listo',
  'shoot.done.body':
    'Tu video se subió. Le agregamos la narración y lo enviamos al cliente — verás el estado en Mis videos.',
  'shoot.done.again': 'Grabar la siguiente tarea',

  'shoot.failed.title': 'La grabación no se guardó',
  'shoot.failed.retake': 'Grabar de nuevo',
  'shoot.failed.quarantine':
    'Esta toma no pasó la verificación automática. Quedó guardada para revisión — por favor graba la tarea otra vez.',
  'shoot.failed.integrity': 'La grabación está dañada — el video es más corto que el cronómetro. Hay que grabarlo otra vez.',
  'shoot.health.warn':
    'La cámara entrega solo {fps} cuadros por segundo — esta toma será rechazada. Busca más luz y vuelve a empezar.',
  'shoot.health.lag':
    'El teléfono no alcanza — el video queda más corto que el cronómetro y esta toma será rechazada. Cierra otras apps o reinicia la grabación.',
  'shoot.health.help.title': 'Qué significa el porcentaje',
  'shoot.health.help.what': 'Cuánto de los últimos 30 segundos realmente quedó en el video.',
  'shoot.health.help.norm': '100% — se grabó todo. Por debajo de 90% el video no se acepta.',
  'shoot.health.help.fix':
    'Si baja: el teléfono no da abasto — cierra otras apps. Un cuarto oscuro afecta la fluidez, no el porcentaje.',
  'shoot.failed.generic': 'La grabación se interrumpió. Hay que grabarla otra vez.',
  'shoot.failed.camera': 'No se pudo abrir la cámara. Revisa el permiso de cámara.',
  'shoot.failed.upload': 'No se pudo terminar la subida. Revisa la conexión e inténtalo de nuevo.',

  'shoot.pending.title': 'Grabación sin terminar',
  'shoot.pending.body':
    'El video anterior no terminó de subirse. Puedes descartarlo y grabar de nuevo — no se guarda nada en el servidor.',
  'shoot.pending.discard': 'Descartar y grabar de nuevo',

  'mine.title': 'Mis videos',
  'mine.accepted': 'aceptado',
  'mine.pending': 'esperando veredicto',
  'mine.rejected': 'no aceptado',
  'mine.filmed': 'grabado',
  'mine.earned': 'ganado',
  'mine.norate': 'tarifa aún sin definir',
  'mine.perhour': 'a {rate}/h',
  'mine.empty': 'Todavía nada — graba tu primer video.',
  'mine.min': 'min',
  'mine.hours': 'h',
  'mine.byDay': 'Por día',
  'mine.all': 'Todos',
  'mine.rejectedTab': 'No aceptados',
  'mine.pendingNote': '{hours} esperando al cliente — pasan a aceptados cuando llegue el veredicto.',
  'mine.noname': 'sin nombre',
  'mine.col.file': 'Archivo',
  'mine.col.when': 'Grabado',
  'mine.col.length': 'Duración',
  'mine.col.status': 'Estado',
  'mine.col.topic': 'Tema',
  'mine.col.reason': 'Motivo',
  'mine.play': 'Ver',
  'mine.player.close': 'Cerrar',

  'status.recording': 'grabando',
  'status.filmed': 'grabado',
  'status.needs_voiceover': 'preparando narración',
  'status.voiced': 'narrado',
  'status.delivered': 'enviado al cliente',
  'status.pending': 'esperando veredicto',
  'status.accepted': 'aceptado',
  'status.rejected': 'no aceptado',
  'status.quarantine': 'en revisión',
  'mine.ledger.title': 'Detalle de ganancias',
  'mine.ledger.what': 'Por',
  'mine.ledger.amount': 'Monto',

  'err.tasks': 'No se pudo cargar la lista de temas — recarga la página.',
  'err.stats': 'Las estadísticas no están disponibles ahora — inténtalo más tarde.',
  'loading': 'Cargando…',
};

const pt: Dict = {
  'shoot.audio.saved': 'O áudio foi gravado numa faixa separada ({kb} KB).',
  'shoot.audio.failed': 'O áudio foi gravado mas não foi enviado — avise a equipe.',
  'mine.noRejected': 'Não há gravações rejeitadas.',
  'mine.noRecords': 'Ainda não há gravações — elas aparecem após a sua primeira filmagem.',

  'login.lede': 'Para a equipe e os colaboradores. O acesso é concedido por um administrador.',
  'login.email': 'e-mail',
  'login.password': 'senha',
  'login.submit': 'Entrar',
  'login.signingIn': 'Entrando…',
  'login.failed': 'Não foi possível entrar: o e-mail ou a senha não conferem. As novas credenciais são entregues pelo seu responsável.',
  'login.build': 'versão',

  'shoot.pending.keep': 'Guardar esta gravação',
  'shoot.pending.another': 'Gravar outra tarefa',
  'shoot.pending.kept': 'Guardado. A equipe vai olhar e decidir — você não precisa fazer mais nada.',
  'shoot.pending.keptEmpty': 'Não havia nada para guardar nessa gravação — dá para gravar de novo.',
  'shoot.failed.kept': 'O que você gravou está guardado — a equipe vai olhar. Nada foi apagado.',
  'pwa.install.title': 'Instale o Vista no seu celular',
  'pwa.install.body': 'Abre com um toque, tela cheia para gravar, sem barras do navegador.',
  'pwa.install.button': 'Instalar',
  'pwa.install.how': 'Como instalar',
  'pwa.install.later': 'Agora não',
  'pwa.ios.1': 'Toque no botão Compartilhar no Safari (o quadrado com a seta).',
  'pwa.ios.2': 'Role para baixo e toque em "Adicionar à Tela de Início".',
  'pwa.ios.3': 'Toque em "Adicionar" — o Vista aparece na sua tela de início.',
  'pwa.update.body': 'Uma nova versão do Vista está pronta.',
  'pwa.update.reload': 'Atualizar',
  'nav.shoot': 'Gravar',
  'nav.mine': 'Meus vídeos',
  'nav.logout': 'Sair',
  'nav.profile': 'Menu da conta',

  'profile.lang': 'Idioma',
  'profile.password': 'Alterar senha',
  'profile.password.new': 'Nova senha',
  'profile.password.confirm': 'Confirme a senha',
  'profile.password.tooShort': 'Pelo menos 8 caracteres',
  'profile.password.mismatch': 'As senhas não coincidem',
  'profile.password.save': 'Salvar',
  'profile.password.saving': 'Salvando…',
  'profile.password.done': 'Senha atualizada',
  'profile.password.cancel': 'Cancelar',

  'nav.upload': 'Enviar',
  'upload.title': 'Enviar um vídeo',
  'upload.hint': 'Gravou com a câmera do celular? Envie o arquivo aqui — mesmos temas, mesmo pagamento. Use isto se o gravador do app der problema.',
  'upload.file': 'Arquivo de vídeo',
  'upload.progress': 'Enviando… {percent}%',
  'upload.more': 'Enviar outro',
  'upload.tooLong': 'O vídeo tem mais de 20 minutos — divida e envie as partes separadamente.',
  'upload.failed': 'O envio não terminou. Verifique a conexão e tente de novo — nada foi salvo.',

  'shoot.title': 'Gravar um vídeo',
  'shoot.topic': 'Tema',
  'shoot.start': 'Começar a gravar',
  'shoot.stop': 'Parar',
  'shoot.block': 'bloco',
  'shoot.uploaded': 'enviado',
  'shoot.lagging': 'o envio está atrasado em',
  'shoot.finishing': 'Enviando o último trecho — não feche esta página…',
  'shoot.length':
    'Grave entre {min} e {max} minutos. Uma gravação longa é dividida em blocos de {seg} minutos, assim uma queda de conexão custa um bloco e não a sessão inteira.',

  'shoot.check.1': 'Câmera na altura dos olhos, inclinada para baixo — suas mãos aparecem',
  'shoot.check.2': 'Luz suficiente, os objetos são bem visíveis',
  'shoot.check.3': 'Trabalho real, gravado do início ao fim',
  'shoot.check.4': 'Sem repetir a mesma ação várias vezes',
  'shoot.check.5': 'Sem pessoas estranhas, documentos ou telas no quadro',

  'shoot.done.title': 'Pronto',
  'shoot.done.body':
    'Seu vídeo foi enviado. Nós adicionamos a narração e mandamos para o cliente — o status aparece em Meus vídeos.',
  'shoot.done.again': 'Gravar a próxima tarefa',

  'shoot.failed.title': 'A gravação não foi salva',
  'shoot.failed.retake': 'Gravar de novo',
  'shoot.failed.quarantine':
    'Esta tomada não passou na verificação automática. Ficou guardada para revisão — por favor grave a tarefa de novo.',
  'shoot.failed.integrity': 'A gravação está corrompida — o vídeo é mais curto que o cronômetro. Precisa gravar de novo.',
  'shoot.health.warn':
    'A câmera está entregando só {fps} quadros por segundo — esta tomada será rejeitada. Procure mais luz e recomece.',
  'shoot.health.lag':
    'O telefone não está dando conta — o vídeo fica mais curto que o cronômetro e esta tomada será rejeitada. Feche outros apps ou recomece a gravação.',
  'shoot.health.help.title': 'O que significa a porcentagem',
  'shoot.health.help.what': 'Quanto dos últimos 30 segundos realmente entrou no vídeo.',
  'shoot.health.help.norm': '100% — tudo foi gravado. Abaixo de 90% o vídeo não é aceito.',
  'shoot.health.help.fix':
    'Se está caindo: o celular não está dando conta — feche outros apps. Um ambiente escuro afeta a fluidez, não a porcentagem.',
  'shoot.failed.generic': 'A gravação foi interrompida. Precisa gravar de novo.',
  'shoot.failed.camera': 'Não foi possível abrir a câmera. Verifique a permissão de câmera.',
  'shoot.failed.upload': 'Não foi possível concluir o envio. Verifique a conexão e tente de novo.',

  'shoot.pending.title': 'Gravação inacabada',
  'shoot.pending.body':
    'O vídeo anterior não terminou de subir. Você pode descartá-lo e gravar de novo — nada fica salvo no servidor.',
  'shoot.pending.discard': 'Descartar e gravar de novo',

  'mine.title': 'Meus vídeos',
  'mine.accepted': 'aceito',
  'mine.pending': 'aguardando veredito',
  'mine.rejected': 'não aceito',
  'mine.filmed': 'gravado',
  'mine.earned': 'ganho',
  'mine.norate': 'valor ainda não definido',
  'mine.perhour': 'a {rate}/h',
  'mine.empty': 'Nada ainda — grave seu primeiro vídeo.',
  'mine.min': 'min',
  'mine.hours': 'h',
  'mine.byDay': 'Por dia',
  'mine.all': 'Todos',
  'mine.rejectedTab': 'Não aceitos',
  'mine.pendingNote': '{hours} aguardando o cliente — passam a aceitos quando o veredito chegar.',
  'mine.noname': 'sem nome',
  'mine.col.file': 'Arquivo',
  'mine.col.when': 'Gravado',
  'mine.col.length': 'Duração',
  'mine.col.status': 'Status',
  'mine.col.topic': 'Tema',
  'mine.col.reason': 'Motivo',
  'mine.play': 'Ver',
  'mine.player.close': 'Fechar',

  'status.recording': 'gravando',
  'status.filmed': 'gravado',
  'status.needs_voiceover': 'preparando narração',
  'status.voiced': 'narrado',
  'status.delivered': 'enviado ao cliente',
  'status.pending': 'aguardando veredito',
  'status.accepted': 'aceito',
  'status.rejected': 'não aceito',
  'status.quarantine': 'em revisão',
  'mine.ledger.title': 'Detalhe dos ganhos',
  'mine.ledger.what': 'Por',
  'mine.ledger.amount': 'Valor',

  'err.tasks': 'Não foi possível carregar os temas — recarregue a página.',
  'err.stats': 'As estatísticas estão indisponíveis agora — tente mais tarde.',
  'loading': 'Carregando…',
};

const ru: Dict = {
  'staff.build': 'сборка',
  'pace.until': 'до {date} · осталось {days} дн · темп',
  'v.accrued': ' Начислено {amount}$',
  'v.reversed': ', снято обратно {amount}$',
  'v.applied': 'Применено: {n}.{money}',
  'v.skipped': ' Не применилось: {n} — {list}',
  'v.truncated': 'Список длиннее, чем мы разбираем за раз: {n} строк(и) не вошли. Примените этот кусок и вставьте остаток.',
  'q.selected': 'Выбрано: {n}',
  'q.moved': 'Перенесено: {n}',
  'q.skipped': 'пропущено: {n}',
  'q.accrued': 'начислено: ${amount}',
  'q.reversed': 'снято обратно: ${amount}',
  'q.unrated': 'без ставки (не начислено): {n} — задай ставку в карточке',
  'q.diag.drops': 'дропов {n}',
  'mng.th.pendingShort': 'На проверке',
  'mng.noRateCount': 'без ставки: {n}',
  'shoot.audio.saved': 'Звук записан отдельной дорожкой ({kb} КБ).',
  'shoot.audio.failed': 'Звук записан, но не отправился — сообщите штабу.',
  'mine.noRejected': 'Отклонённых записей нет.',
  'mine.noRecords': 'Записей пока нет — они появятся после первой съёмки.',

  'cp.notFound': 'Не нашли этого человека — возможно, он в пуле другого админа.',
  'cp.backToPool': 'Вернуться к пулу',
  'cp.loading': 'Загружаем…',
  'cp.crumb': '← Мой пул',
  'cp.noCountry': 'страна не указана',
  'cp.inviteLink': 'Ссылка для входа',
  'cp.pwd.generating': 'Генерируем…',
  'cp.pwd.new': 'Новый пароль кабинета',
  'cp.unblocking': 'Разблокируем…',
  'cp.unblock': 'Разблокировать',
  'cp.blockConfirm': 'Точно заблокировать?',
  'cp.blocking': 'Блокируем…',
  'cp.yes': 'Да',
  'cp.cancel': 'Отмена',
  'cp.block': 'Заблокировать',
  'cp.cabinet': 'Съёмочный кабинет',
  'cp.linked': 'привязан к штабному аккаунту',
  'cp.linkedTail': '— этот человек переключается между кабинетами через меню профиля.',
  'cp.notLinked': 'ни к кому не привязан. Если это тот же человек, что сидит в штабе, привяжите — тогда у него появится переключатель кабинетов.',
  'cp.unlinking': 'Отвязываем…',
  'cp.unlink': 'Отвязать от меня',
  'cp.linking': 'Привязываем…',
  'cp.itsMe': 'Это я',
  'cp.journal': 'Что делали с человеком и его клипами',
  'cp.th.when': 'Когда',
  'cp.th.who': 'Кто',
  'cp.th.what': 'Что',
  'cp.th.details': 'Подробности',
  'cp.system': 'система',
  'cp.noRateWarn': 'Ставка не задана — принятые часы этого человека не начнут начисляться, пока она не появится.',
  'cp.data': 'Данные',
  'cp.name': 'Имя',
  'cp.country': 'Страна (2 буквы)',
  'cp.city': 'Город',
  'cp.phone': 'Телефон',
  'cp.uiLang': 'Язык интерфейса',
  'cp.saving': 'Сохраняем…',
  'cp.save': 'Сохранить',
  'cp.saved': 'Сохранено',
  'cp.saveFailed': 'Не сохранилось:',
  'cp.noRate': 'ставка не задана',
  'cp.earnedAt': 'заработал по {rate}/ч',
  'cp.byDay': 'По дням',
  'cp.records': 'Записи',
  'cp.tab.all': 'Все ({n})',
  'cp.tab.rejected': 'Отклонённые ({n})',
  'cp.noRejected': 'Отклонённых нет — хорошо.',
  'cp.noRecords': 'Записей пока нет.',
  'cp.th.file': 'Файл',
  'cp.th.filmed': 'Снято',
  'cp.th.length': 'Длина',
  'cp.th.status': 'Статус',
  'cp.th.topic': 'Тема',
  'cp.th.integrity': 'Целостность',
  'cp.th.reason': 'Причина',
  'cp.noName': 'без имени',
  'cp.owners': 'Кто вёл',
  'cp.now': 'сейчас',
  'cp.creds.copied': '✓ Скопировано',
  'cp.creds.copy': 'Скопировать логин-пароль',
  'cp.creds.hide': 'Скрыть',
  'cp.creds.note': 'Новый доступ в кабинет vista.weern.com — показывается один раз.',
  'cp.act.capture_open': 'начата запись',
  'cp.act.capture_finish': 'запись завершена',
  'cp.act.capture_integrity_failed': 'брак по целостности',
  'cp.act.capture_salvaged': 'спасено из брошенной загрузки',
  'cp.act.clip_accept': 'клиент принял',
  'cp.act.clip_reject': 'клиент отклонил',
  'cp.act.clip_deliver': 'отгружено клиенту',
  'cp.act.clip_pending': 'ждём вердикт',
  'cp.act.clip_requeue': 'вернули на озвучку',
  'cp.act.clip_queue_voiceover': 'в очередь озвучки',
  'cp.act.rate_change': 'изменена ставка',
  'cp.act.status_blocked': 'заблокирован',
  'cp.act.status_active': 'разблокирован',
  'cp.act.password_reset': 'выдан новый пароль',
  'cp.act.payout': 'выплата',
  'cp.act.adjust': 'корректировка',
  'cp.rev.recording': 'идёт запись',
  'cp.rev.filmed': 'снято',
  'cp.rev.needs_voiceover': 'ждёт озвучки',
  'cp.rev.voiced': 'озвучено',
  'cp.rev.delivered': 'отгружено',
  'cp.rev.pending': 'ждём вердикт',
  'cp.rev.accepted': 'принято',
  'cp.rev.rejected': 'отклонено',

  'pace.title': 'План к дедлайну',
  'pace.intro': 'Задайте общую цель в часах принятого материала и дедлайн — темп и «нужно в день» дальше считаются сами.',
  'pace.edit': 'изменить цель',
  'pace.loadFailed': 'Не удалось загрузить план — попробуйте обновить страницу.',
  'pace.noGoal': 'Цель ещё не задана менеджером.',
  'pace.onTrack': 'Успеваем',
  'pace.behind': 'Отстаём',
  'pace.perDay': 'ч/день',
  'pace.need': 'нужно принимать',
  'pace.perDay7': 'ч/день (7 дн)',
  'pace.accepted': 'принято',
  'pace.of': 'ч из',
  'pace.h': 'ч',
  'pace.goal': 'Цель, часов принятого материала',
  'pace.goalPh': 'например 500',
  'pace.deadline': 'Дедлайн',
  'pace.cancel': 'Отмена',
  'pace.saving': 'Сохраняем…',
  'pace.save': 'Сохранить цель',
  'pace.err.goal': 'Цель должна быть положительным числом часов.',
  'pace.err.deadline': 'Дедлайн — дата в формате ГГГГ-ММ-ДД.',
  'pace.err.save': 'Не удалось сохранить цель.',
  'live.nobody': 'Сейчас никто не снимает',
  'live.now': '{n} {word} сейчас',
  'live.recording.one': 'пишет',
  'live.recording.many': 'пишут',
  'live.clipsToday': 'клипов сегодня',
  'live.filmedToday': 'снято сегодня',
  'live.peopleFilmed': 'человек снимали',
  'live.mb': '{n} МБ',
  'inv.title': 'Ссылка для входа · {name}',
  'inv.note': 'Действует 7 дней. Отправь человеку — он откроет на своём телефоне и сразу попадёт в кабинет, пароль не нужен.',
  'inv.copied': 'Скопировано',
  'inv.copy': 'Скопировать ссылку',
  'inv.failed': 'Не удалось создать ссылку. Попробуй ещё раз.',
  'inv.creating': 'Создаём…',
  'inv.close': 'Закрыть',
  'player.preparing': 'Готовим просмотр…',

  'fin.type.accrual': 'начислено',
  'fin.type.advance': 'аванс',
  'fin.type.payout': 'выплата',
  'fin.type.adjustment': 'корректировка',
  'fin.loading': 'Загружаем…',
  'fin.loadFailed': 'Не удалось загрузить балансы.',
  'fin.historyFailed': 'Историю не удалось загрузить.',
  'fin.moreFailed': 'Не удалось догрузить историю.',
  'fin.tile.owed': 'должны всего',
  'fin.tile.withDebt': 'с непогашенным долгом',
  'fin.tile.noRate': 'приняты, но без ставки',
  'fin.contributors': 'Контрибьюторы',
  'fin.staff': 'Штаб (маржа)',
  'fin.nobody': 'Пока никого.',
  'fin.th.who': 'Кто',
  'fin.th.rate': 'Ставка',
  'fin.th.accepted': 'Принято, ч',
  'fin.th.owed': 'Должны',
  'fin.noRate': 'не задана',
  'fin.history': 'история →',
  'fin.owed': 'Должны:',
  'fin.rateSuffix': ' · ставка {rate}/ч',
  'fin.payAmount': 'Выплатить, $',
  'fin.pay': 'Записать выплату',
  'fin.adjust.hide': 'Скрыть корректировку',
  'fin.adjust': 'Корректировка',
  'fin.err.amount': 'Введи сумму больше нуля.',
  'fin.err.duplicatePayout': 'Такая выплата уже записана — повтор не сохранён.',
  'fin.err.notSaved': 'Не сохранилось.',
  'fin.th.when': 'Когда',
  'fin.th.what': 'Что',
  'fin.th.amount': 'Сумма',
  'fin.th.note': 'Комментарий',
  'fin.th.by': 'Кто сделал',
  'fin.noMoves': 'Движений ещё не было.',
  'fin.more': 'Ещё (осталось {n})',
  'fin.close': 'Закрыть',
  'fin.adjust.amount': 'Сумма корректировки, $ (+ уменьшает долг, − увеличивает)',
  'fin.adjust.reason': 'Причина (обязательно)',
  'fin.err.adjustAmount': 'Сумма должна быть ненулевой (со знаком: + или −).',
  'fin.err.adjustReason': 'Укажи причину корректировки — без неё запись не сохранить.',
  'fin.err.duplicateAdjust': 'Такая корректировка уже записана — повтор не сохранён.',
  'fin.saving': 'Сохраняем…',
  'fin.adjust.apply': 'Применить корректировку',

  'v.title': 'Вердикты клиента списком',
  'v.help': 'Вставьте таблицу как есть: имя файла, вердикт, причина. Подойдут таб, запятая или точка с запятой; шапка и кавычки не мешают. Ничего не изменится, пока вы не посмотрите предпросмотр и не нажмёте «Применить».',
  'v.close': 'Закрыть',
  'v.parsing': 'Разбираем…',
  'v.check': 'Проверить список',
  'v.willApply': 'Применится:',
  'v.needAttention': '· требуют внимания: {n} (не применятся)',
  'v.th.line': 'Строка',
  'v.th.clip': 'Клип',
  'v.th.who': 'Кто снял',
  'v.th.verdict': 'Вердикт',
  'v.th.problem': 'Что не так',
  'v.notFound': 'не найден',
  'v.accepted': 'принят',
  'v.rejected': 'отклонён',
  'v.ambiguous': 'подходит нескольким клипам',
  'v.notInPool': 'клип не найден в вашем пуле',
  'v.badVerdict': 'вердикт в строке не распознан',
  'v.back': 'Назад к тексту',
  'v.applying': 'Применяем…',
  'v.apply': 'Применить {n}',
  'v.reason.integrity': 'целостность',
  'v.reason.no_narration': 'нет озвучки',
  'v.reason.weak_narration': 'слабая озвучка',
  'v.reason.hands_out_of_frame': 'руки вне кадра',
  'v.reason.poor_light': 'плохой свет',
  'v.reason.motion_blur': 'смаз',
  'v.reason.repetitive': 'повторы',
  'v.reason.staged': 'постановка',
  'v.reason.privacy': 'приватность',
  'v.reason.duplicate': 'дубль',
  'v.reason.off_topic': 'не по теме',
  'v.reason.other': 'другое',

  'q.reject.title': 'Отклонить {count} шт.',
  'q.tab.needs_voiceover': 'На озвучку',
  'q.tab.voiced': 'К отгрузке',
  'q.tab.delivered': 'Отгружено',
  'q.tab.pending': 'Ждём вердикт',
  'q.tab.accepted': 'Принято',
  'q.tab.rejected': 'Отклонено',
  'q.tab.quarantine': 'Карантин',
  'q.reason.integrity': 'Битая запись (короче таймера)',
  'q.reason.no_narration': 'Нет озвучки',
  'q.reason.weak_narration': 'Озвучка не про то, что в кадре',
  'q.reason.hands_out_of_frame': 'Руки вне кадра',
  'q.reason.poor_light': 'Плохой свет',
  'q.reason.motion_blur': 'Смаз, не видно действия',
  'q.reason.repetitive': 'Одно и то же действие подряд',
  'q.reason.staged': 'Наигранно, постановка',
  'q.reason.privacy': 'Приватность: люди, документы, экраны',
  'q.reason.duplicate': 'Повтор уже сданного',
  'q.reason.off_topic': 'Не та тема',
  'q.reason.other': 'Другое',
  'q.saveFailed': 'Не сохранилось. Попробуй ещё раз.',
  'q.voiced.uploaded': 'Озвученный файл загружен — клип перешёл в «К отгрузке».',
  'q.voiced.failed': 'Файл не загрузился.',
  'q.move.deliver': 'Отгрузить клиенту',
  'q.move.accept': 'Клиент принял',
  'q.move.requeue': 'Вернуть на озвучку',
  'q.move.rehab': 'Материал годен — в озвучку',
  'q.move.reject': 'Отклонить',
  'q.loading': 'Загружаем…',
  'q.loadFailed': 'Не удалось загрузить очередь.',
  'q.search': 'Поиск: контрибьютор, почта, тема, id клипа…',
  'q.verdicts': 'Вердикты списком',
  'q.allContributors': 'Все контрибьюторы',
  'q.from': 'С',
  'q.to': 'по',
  'q.empty': 'Пусто — в этой стопке сейчас ничего нет.',
  'q.th.contributor': 'Контрибьютор',
  'q.th.topic': 'Тема',
  'q.th.length': 'Длина',
  'q.th.size': 'Размер',
  'q.th.source': 'Источник',
  'q.th.integrity': 'Целостность',
  'q.th.filmed': 'Снято',
  'q.th.shipped': 'Отгружено',
  'q.th.files': 'Файлы',
  'q.th.reason': 'Причина',
  'q.source.upload': 'файл',
  'q.source.recorder': 'рекордер',
  'q.integrity.na': 'не применима',
  'q.watch': '▶ смотреть',
  'q.file.raw': 'исходник',
  'q.file.voiced': 'озвученный',
  'q.audio.pending': 'звук сводится',
  'q.audio.none': 'без звука',
  'q.uploadVoiced': 'залить озвучку',
  'q.comment': 'Комментарий (виден контрибьютору)',
  'q.cancel': 'Отмена',
  'q.close': 'Закрыть',

  'mng.creds.copied': '✓ Скопировано',
  'mng.creds.copy': 'Скопировать логин-пароль',
  'mng.creds.hide': 'Скрыть',
  'mng.creds.note': 'Пароль показывается один раз — дальше только смена.',
  'mng.creds.text': 'vista.weern.com\nлогин: {email}\nпароль: {password}',
  'mng.saveFailed': 'Не сохранилось:',
  'mng.admins': 'Админы и пулы',
  'mng.loading': 'Загружаем…',
  'mng.th.staff': 'Сотрудник',
  'mng.th.role': 'Роль',
  'mng.th.rate': 'Ставка $/ч',
  'mng.th.people': 'Людей',
  'mng.th.accepted': 'Принято ч',
  'mng.th.pending': 'На проверке',
  'mng.th.rejected': 'Брак ч',
  'mng.th.toContributors': 'Контрибьюторам',
  'mng.th.margin': 'Маржа',
  'mng.disabled': 'выключен',
  'mng.unowned': 'Бесхозные',
  'mng.allAssigned': 'Все контрибьюторы разобраны по пулам.',
  'mng.th.status': 'Статус',
  'mng.th.assign': 'Назначить',
  'mng.pickAdmin': '— выбрать админа —',
  'mng.staff': 'Сотрудники',
  'mng.search': 'Поиск по email или имени…',
  'mng.filter.all': 'Все',
  'mng.filter.enabled': 'Активны',
  'mng.filter.disabled': 'Выключены',
  'mng.noStaff': 'Сотрудников пока нет.',
  'mng.noMatch': 'Никто не подходит под фильтр.',
  'mng.th.name': 'Имя',
  'mng.th.pool': 'Пул',
  'mng.th.enabled': 'Активен',
  'mng.th.seesMoney': 'Видит деньги',
  'mng.th.password': 'Пароль',
  'mng.pwd.new': 'новый (мин. 8)',
  'mng.pwd.generate': 'Сгенерировать',
  'mng.pwd.save': 'сохранить',
  'mng.pwd.change': 'сменить',
  'mng.pwd.tooShort': 'Пароль — минимум 8 символов.',
  'mng.new.email': 'email нового сотрудника',
  'mng.new.name': 'имя',
  'mng.new.password': 'пароль (мин. 8)',
  'mng.new.generate': 'Сгенерировать пароль',
  'mng.new.creating': 'Создаём…',
  'mng.new.submit': '+ Сотрудник',
  'mng.role.viewer': 'просмотр',
  'mng.role.admin': 'админ',
  'mng.role.manager': 'менеджер',

  'pool.err.noAdminRate': 'Ставки контрибьюторам станут доступны, когда менеджер назначит твою ставку.',
  'pool.err.aboveAdmin': 'Ставка контрибьютора должна быть ниже твоей{max}.',
  'pool.err.belowPool': 'В пуле этого админа уже есть ставка{max} — ставка админа должна оставаться выше. Понизить её получится после снижения ставок контрибьюторов.',
  'pool.ago.today': 'сегодня',
  'pool.ago.yesterday': 'вчера',
  'pool.ago.days': '{n} дн назад',
  'pool.status.invited': 'приглашён',
  'pool.status.joined': 'вошёл',
  'pool.status.active': 'активен',
  'pool.status.blocked': 'заблокирован',
  'pool.tile.accepted': 'принято',
  'pool.tile.pending': 'на проверке',
  'pool.tile.rejected': 'брак',
  'pool.tile.revenue': 'доход',
  'pool.tile.filming': 'снимают',
  'pool.hours': 'ч',
  'pool.strip.toDub': '🎙 {hours} ч ({files} файл(ов)) ждут озвучки.',
  'pool.strip.toShip': '📦 {hours} ч ({files} файл(ов)) озвучены и ждут отгрузки клиенту.',
  'pool.filtered': 'Пул:',
  'pool.showAll': 'показать всех',
  'pool.add': '+ Добавить людей',
  'pool.search': 'Поиск по email или имени…',
  'pool.status.all': 'Все статусы',
  'pool.owner.all': 'Все админы',
  'pool.loading': 'Загружаем…',
  'pool.empty': 'В пуле пока никого — новых людей можно добавить кнопкой сверху.',
  'pool.noMatch': 'Никто не подходит под фильтр.',
  'pool.th.contributor': 'Контрибьютор',
  'pool.th.status': 'Статус',
  'pool.th.antifraud': 'Анти-фрод',
  'pool.th.owner': 'Владелец',
  'pool.th.accepted': 'Принято ч',
  'pool.th.pending': 'Ожидает ч',
  'pool.th.rejected': 'Брак ч',
  'pool.th.quality': 'Качество',
  'pool.th.rate': 'Ставка $/ч',
  'pool.th.earned': 'Заработал',
  'pool.th.activity': 'Активность',
  'pool.th.revenue': 'Доход',
  'pool.creds.copied': '✓ Скопировано',
  'pool.creds.copy': 'Скопировать все ({n})',
  'pool.creds.note': 'Доступы в кабинет vista.weern.com. Показываются один раз — дальше только смена пароля на карточке.',
  'pool.dlg.title': 'Добавить контрибьюторов',
  'pool.dlg.emails': 'Email\'ы (до 25, через запятую или с новой строки)',
  'pool.dlg.rate': 'Ставка, $/час принятого (можно оставить пустым и задать позже)',
  'pool.dlg.ratePh': 'например 6',
  'pool.dlg.over25': '⚠ Больше 25 — уйдут только первые 25, остальные добавятся второй пачкой.',
  'pool.dlg.result': 'Добавлено новых: {created}, уже были: {existing}.',
  'pool.dlg.foreign': 'Эти люди уже закреплены за другим админом, перевод делает менеджер:',
  'pool.dlg.error': 'Ошибка:',
  'pool.dlg.close': 'Закрыть',
  'pool.dlg.cancel': 'Отмена',
  'pool.dlg.adding': 'Добавляем…',
  'pool.dlg.submit': 'Добавить ({n})',

  'staff.sub': '· дашборд',
  'staff.loading': 'Загружаем…',
  'staff.tab.team': 'Команда',
  'staff.tab.queue': 'Очередь',
  'staff.tab.money': 'Деньги',
  'staff.tab.manage': 'Управление',
  'staff.role.manager': 'Менеджер',
  'staff.role.admin': 'Админ',
  'staff.role.viewer': 'Наблюдатель',
  'staff.role.contributor': 'Контрибьютор',
  'staff.cabinet': 'Кабинет: {role}',
  'staff.password': 'Сменить пароль',
  'staff.password.new': 'Новый пароль',
  'staff.password.confirm': 'Повторите пароль',
  'staff.password.tooShort': 'Минимум 8 символов',
  'staff.password.mismatch': 'Пароли не совпадают',
  'staff.password.cancel': 'Отмена',
  'staff.password.save': 'Сохранить',
  'staff.password.saving': 'Сохраняем…',
  'staff.password.done': 'Пароль изменён',
  'staff.logout': 'Выйти',
  'staff.lang': 'Язык',

  'login.lede': 'Вход для сотрудников и контрибьюторов. Доступ выдаёт админ.',
  'login.email': 'почта',
  'login.password': 'пароль',
  'login.submit': 'Войти',
  'login.signingIn': 'Входим…',
  'login.failed': 'Войти не получилось: почта или пароль не подходят. Новые данные для входа выдаёт менеджер.',
  'login.build': 'сборка',

  'shoot.pending.keep': 'Сохранить эту запись',
  'shoot.pending.another': 'Снять другое задание',
  'shoot.pending.kept': 'Сохранено. Штаб посмотрит и решит — от тебя больше ничего не нужно.',
  'shoot.pending.keptEmpty': 'В той записи сохранять было нечего — её можно снять заново.',
  'shoot.failed.kept': 'Отснятое сохранено — штаб посмотрит. Ничего не удалено.',
  'pwa.install.title': 'Установи Vista на телефон',
  'pwa.install.body': 'Открывается одним касанием, во весь экран для съёмки, без панелей браузера.',
  'pwa.install.button': 'Установить',
  'pwa.install.how': 'Как установить',
  'pwa.install.later': 'Не сейчас',
  'pwa.ios.1': 'Нажми кнопку «Поделиться» в Safari (квадрат со стрелкой).',
  'pwa.ios.2': 'Пролистай вниз и нажми «На экран «Домой»».',
  'pwa.ios.3': 'Нажми «Добавить» — Vista появится на экране «Домой».',
  'pwa.update.body': 'Готова новая версия Vista.',
  'pwa.update.reload': 'Обновить',
  'nav.shoot': 'Съёмка',
  'nav.mine': 'Мои клипы',
  'nav.logout': 'Выйти',
  'nav.profile': 'Меню аккаунта',

  'profile.lang': 'Язык',
  'profile.password': 'Сменить пароль',
  'profile.password.new': 'Новый пароль',
  'profile.password.confirm': 'Подтверди пароль',
  'profile.password.tooShort': 'Минимум 8 символов',
  'profile.password.mismatch': 'Пароли не совпадают',
  'profile.password.save': 'Сохранить',
  'profile.password.saving': 'Сохраняем…',
  'profile.password.done': 'Пароль изменён',
  'profile.password.cancel': 'Отмена',

  'nav.upload': 'Загрузка',
  'upload.title': 'Загрузить видео',
  'upload.hint': 'Снял на камеру телефона? Загрузи файл здесь — те же темы, та же оплата. Используй это, если рекордер в приложении подводит.',
  'upload.file': 'Видеофайл',
  'upload.progress': 'Загружаем… {percent}%',
  'upload.more': 'Загрузить ещё',
  'upload.tooLong': 'Видео длиннее 20 минут — раздели его и загрузи части по отдельности.',
  'upload.failed': 'Загрузка не завершилась. Проверь соединение и попробуй снова — ничего не сохранилось.',

  'shoot.title': 'Снять клип',
  'shoot.topic': 'Тема',
  'shoot.start': 'Начать запись',
  'shoot.stop': 'Стоп',
  'shoot.block': 'блок',
  'shoot.uploaded': 'загружено',
  'shoot.lagging': 'заливка отстаёт на',
  'shoot.finishing': 'Догружаем последний кусок — не закрывай эту страницу…',
  'shoot.length':
    'Снимай от {min} до {max} минут. Длинный дубль делится на блоки по {seg} минут — обрыв связи стоит один блок, а не всю сессию.',

  'shoot.check.1': 'Камера на уровне глаз, наклонена вниз — руки видны в кадре',
  'shoot.check.2': 'Достаточно света, предметы хорошо различимы',
  'shoot.check.3': 'Реальная работа, снятая от начала до конца',
  'shoot.check.4': 'Без повторения одного и того же действия по кругу',
  'shoot.check.5': 'Без посторонних людей, документов и экранов в кадре',

  'shoot.done.title': 'Готово',
  'shoot.done.body':
    'Клип загружен. Мы добавим озвучку и отправим клиенту — статус увидишь в «Мои клипы».',
  'shoot.done.again': 'Снять следующую задачу',

  'shoot.failed.title': 'Запись не сохранилась',
  'shoot.failed.retake': 'Снять заново',
  'shoot.failed.quarantine':
    'Этот дубль не прошёл автоматическую проверку. Он сохранён для разбора штабом — пожалуйста, сними задачу ещё раз.',
  'shoot.failed.integrity': 'Запись повреждена — видео короче, чем таймер. Сними её заново.',
  'shoot.health.warn':
    'Камера отдаёт только {fps} кадров в секунду — этот дубль будет отклонён. Добавь света или перейди в более светлое место и начни заново.',
  'shoot.health.lag':
    'Телефон не успевает — видео отстаёт от таймера, и этот дубль будет отклонён. Закрой другие приложения или начни запись заново.',
  'shoot.health.help.title': 'Что значит процент',
  'shoot.health.help.what': 'Сколько из последних 30 секунд реально попало в видео.',
  'shoot.health.help.norm': '100% — всё записалось. Ниже 90% клип не примут.',
  'shoot.health.help.fix':
    'Если падает: телефон не успевает — закрой другие приложения. Тёмное помещение снижает плавность, но не долю.',
  'shoot.failed.generic': 'Запись прервалась. Сними её заново.',
  'shoot.failed.camera': 'Не удалось включить камеру. Проверь разрешение на камеру.',
  'shoot.failed.upload': 'Не удалось завершить загрузку. Проверь соединение и попробуй снова.',

  'shoot.pending.title': 'Незавершённая запись',
  'shoot.pending.body':
    'Предыдущий клип не успел загрузиться. Можно удалить его и снять заново — на сервере ничего не остаётся.',
  'shoot.pending.discard': 'Удалить и снять заново',

  'mine.title': 'Мои клипы',
  'mine.accepted': 'принято',
  'mine.pending': 'ждёт вердикта',
  'mine.rejected': 'не принято',
  'mine.filmed': 'снято',
  'mine.earned': 'заработано',
  'mine.norate': 'ставка ещё не назначена',
  'mine.perhour': 'по {rate}/ч',
  'mine.empty': 'Пока ничего нет — сними первый клип.',
  'mine.min': 'мин',
  'mine.hours': 'ч',
  'mine.byDay': 'По дням',
  'mine.all': 'Все',
  'mine.rejectedTab': 'Не приняты',
  'mine.pendingNote': '{hours} ещё ждут клиента — перейдут в принятые, как только придёт вердикт.',
  'mine.noname': 'без имени',
  'mine.col.file': 'Файл',
  'mine.col.when': 'Снято',
  'mine.col.length': 'Длина',
  'mine.col.status': 'Статус',
  'mine.col.topic': 'Тема',
  'mine.col.reason': 'Причина',
  'mine.play': 'Смотреть',
  'mine.player.close': 'Закрыть',

  'status.recording': 'идёт запись',
  'status.filmed': 'снято',
  'status.needs_voiceover': 'готовим озвучку',
  'status.voiced': 'озвучено',
  'status.delivered': 'отправлено клиенту',
  'status.pending': 'ждёт вердикта',
  'status.accepted': 'принято',
  'status.rejected': 'не принято',
  'status.quarantine': 'на проверке',
  'mine.ledger.title': 'Детализация начислений',
  'mine.ledger.what': 'За что',
  'mine.ledger.amount': 'Сумма',

  'err.tasks': 'Не удалось загрузить список тем — попробуй перезагрузить страницу.',
  'err.stats': 'Статистика сейчас недоступна — попробуй немного позже.',
  'loading': 'Загрузка…',
};

const DICTS: Record<Lang, Dict> = { en, es, pt, ru };

/**
 * Перевод по ключу. Отсутствующий ключ возвращается как есть, а не падает и не
 * рисует пустоту: недостающая строка должна быть заметна в интерфейсе, но не
 * должна ломать экран, на котором человек стоит с телефоном на лбу.
 */
export function makeT(lang: Lang) {
  const dict = DICTS[lang] ?? en;
  return (key: string, vars?: Record<string, string | number>): string => {
    let s = dict[key] ?? en[key] ?? key;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
    return s;
  };
}

export type T = ReturnType<typeof makeT>;

/** Ярлык темы на языке человека, с откатом на английский оригинал каталога. */
export function taskTitle(
  lang: Lang,
  t: { titleEn: string; titleEs: string | null; titlePt: string | null; titleRu?: string | null },
): string {
  if (lang === 'es') return t.titleEs ?? t.titleEn;
  if (lang === 'pt') return t.titlePt ?? t.titleEn;
  if (lang === 'ru') return t.titleRu ?? t.titleEn;
  return t.titleEn;
}
